import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import moment from "moment";
import { Search, FileText } from "lucide-react";
import { getPrescriptions } from "../../api/api";
import DataTable from "../../components/DataTable";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { PRESCRIPTION_STATUS_LABEL } from "../../lib/prescription-constants";

const STATUS_VARIANT = { draft: "outline", completed: "secondary" };

export default function Prescriptions() {
  const navigate = useNavigate();
  const [prescriptions, setPrescriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [meta, setMeta] = useState({ total: 0, numberOfPage: 1 });

  useEffect(() => {
    setLoading(true);
    const t = setTimeout(() => {
      getPrescriptions({ search: search || undefined, page, limit: 15 })
        .then((res) => {
          setPrescriptions(res.data);
          setMeta({ total: res.total, numberOfPage: res.numberOfPage });
        })
        .catch((err) => toast.error(err?.message || "Could not load prescriptions."))
        .finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(t);
  }, [search, page]);

  const columns = [
    {
      key: "patient",
      header: "Patient",
      render: (p) => (
        <div>
          <p className="font-medium">{p.patient_id?.name}</p>
          <p className="text-xs text-muted-foreground">{p.patient_id?.phone}</p>
        </div>
      ),
    },
    { key: "doctor", header: "Doctor", render: (p) => p.doctor_id?.name },
    { key: "date", header: "Date", render: (p) => moment(p.createdAt).format("D MMM YYYY") },
    {
      key: "diagnosis",
      header: "Diagnosis",
      render: (p) => (
        <span className="text-sm text-muted-foreground">
          {p.diagnoses?.length ? p.diagnoses.map((d) => d.name).join(", ") : "—"}
        </span>
      ),
    },
    {
      key: "medicines",
      header: "Medicines",
      render: (p) => <span className="tabular-nums text-sm">{p.medicines?.length || 0}</span>,
    },
    {
      key: "status",
      header: "Status",
      render: (p) => <Badge variant={STATUS_VARIANT[p.status]}>{PRESCRIPTION_STATUS_LABEL[p.status]}</Badge>,
    },
    {
      key: "actions",
      header: "",
      render: (p) => (
        <div className="flex justify-end">
          <Button size="sm" variant="outline" className="bg-card"
            onClick={() => navigate(`/prescriptions/${p.appointment_id}`)}>
            <FileText className="h-3.5 w-3.5" /> Open
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="font-serif text-2xl font-semibold">Prescriptions</h2>
          <p className="text-sm text-muted-foreground">
            Every prescription written from the Appointments desk
          </p>
        </div>
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          placeholder="Search by patient name or phone…"
          className="h-9 pl-9"
        />
      </div>

      <DataTable
        columns={columns}
        data={prescriptions}
        loading={loading}
        emptyMessage="No prescriptions written yet — start one from an appointment."
        page={page}
        numberOfPage={meta.numberOfPage}
        onPageChange={setPage}
        rowKey={(p) => p._id}
      />
    </div>
  );
}
